import React, { useState, useRef, useEffect } from 'react';
import { MacroCurriculum } from '@/services/roadmap/roadmapService';
import { DEFAULT_BELTS, Belt } from './types';
import { TimelineControls } from './TimelineControls';
import { BeltCircles } from './MacroBeltCircles';
import { TimelineTrack } from './MacroTimelineTrack';
import { CurriculumDialog } from '../CurriculumDialog';
import { usePermissions } from '@/contexts/unified-permissions';

interface MacroBeltTimelineProps {
  data: MacroCurriculum[];
}

export const MacroBeltTimeline: React.FC<MacroBeltTimelineProps> = ({ data }) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [containerWidth, setContainerWidth] = useState(0);
  const [selectedBelt, setSelectedBelt] = useState<Belt | null>(null);
  const [selectedCurriculum, setSelectedCurriculum] = useState<MacroCurriculum | null>(null);
  const { user } = usePermissions();

  useEffect(() => {
    checkScrollability();
    updateContainerWidth();

    const handleResize = () => {
      checkScrollability();
      updateContainerWidth();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (selectedBelt) {
      const curriculum = data.find(item =>
        item.belt_color.toLowerCase() === selectedBelt.dbColor.toLowerCase()
      );
      setSelectedCurriculum(curriculum || null);
    } else {
      setSelectedCurriculum(null);
    }
  }, [selectedBelt, data]);

  const updateContainerWidth = () => {
    if (!scrollContainerRef.current) return;
    setContainerWidth(scrollContainerRef.current.clientWidth);
  };

  const checkScrollability = () => {
    if (!scrollContainerRef.current) return;

    const container = scrollContainerRef.current;
    setCanScrollLeft(container.scrollLeft > 2);
    setCanScrollRight(container.scrollLeft < container.scrollWidth - container.clientWidth - 5);
  };

  const scrollLeft = () => {
    if (!scrollContainerRef.current) return;
    scrollContainerRef.current.scrollBy({ left: -250, behavior: 'smooth' });
    setTimeout(checkScrollability, 300);
  };

  const scrollRight = () => {
    if (!scrollContainerRef.current) return;
    scrollContainerRef.current.scrollBy({ left: 250, behavior: 'smooth' });
    setTimeout(checkScrollability, 300);
  };

  const handleBeltClick = (belt: Belt) => {
    if (selectedBelt && selectedBelt.name === belt.name) {
      setSelectedBelt(null);
    } else {
      setSelectedBelt(belt);
    }
  };

  const handleCloseDialog = () => {
    setSelectedBelt(null);
  };

  const belts = DEFAULT_BELTS;

  // 🧠 Spacing logic with mobile adjustment
  const baseMinSpacing = 72;
  const mobileMinSpacing = 96;
  const isMobile = containerWidth < 640; // Tailwind sm breakpoint
  const MIN_SPACING = isMobile ? mobileMinSpacing : baseMinSpacing;
  const MAX_SPACING = 220;

  const totalBelts = belts.length;
  const availableSpace = containerWidth - (40 + 36); // left offset + arrow width

  const idealSpacing = availableSpace / (totalBelts + 1); // +1 for the arrow space
  const beltSpacing = Math.max(MIN_SPACING, Math.min(MAX_SPACING, idealSpacing));

  // --- BEGIN: auto-scroll logic
  useEffect(() => {
    if (!scrollContainerRef.current || !user) return;

    // Belt rank from profile metadata or fallback to "white"
    const userBeltRank = user.user_metadata?.belt_rank || "white";
    // Only auto-scroll if not white belt with 0 stripes
    if (userBeltRank.toLowerCase() === "white") {
      // Stripes can be 0 or empty or "no stripes"
      const stripes = user.user_metadata?.stripes ?? "0";
      if (stripes === 0 || stripes === "0" || stripes === "" || stripes === "no stripes") {
        // Don't autoscroll for white belt 0 stripes
        return;
      }
    }

    // Find the belt index for user's belt rank
    const userBeltIdx = belts.findIndex(
      (belt) => belt.dbColor.toLowerCase() === userBeltRank.toLowerCase()
    );

    if (userBeltIdx === -1) {
      // If not found, don't scroll
      return;
    }

    // Calculate the X position for that belt
    const startX = 40; // Keep consistent with placement
    const beltPos = startX + userBeltIdx * beltSpacing;

    // Calculate scroll position so user's belt is roughly centered if possible
    const spaceToCenter = containerWidth / 2;
    let scrollTo = beltPos - spaceToCenter;

    // Clamp between 0 and max scroll
    const container = scrollContainerRef.current;
    const maxScroll = container.scrollWidth - containerWidth;
    scrollTo = Math.max(0, Math.min(maxScroll, scrollTo));

    container.scrollTo({ left: scrollTo, behavior: 'smooth' });
  }, [containerWidth, user, belts, beltSpacing]);
  // --- END: auto-scroll logic

  return (
    <div className="relative w-full">
      <TimelineControls 
        onScrollLeft={scrollLeft}
        onScrollRight={scrollRight}
        canScrollLeft={canScrollLeft}
        canScrollRight={canScrollRight}
      />
      
      <div 
        className="overflow-x-auto pb-4 hide-scrollbar w-full mx-auto"
        ref={scrollContainerRef} 
        onScroll={checkScrollability}
        style={{ 
          paddingLeft: '4px',
          paddingRight: '12px',
          scrollbarWidth: 'none',
          scrollPaddingLeft: '0px',
          maxWidth: '100%',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        <div className="relative h-[110px]" style={{ 
          width: '100%', 
          minWidth: '500px'
        }}>
          <TimelineTrack belts={belts} beltSpacing={beltSpacing} />
          <BeltCircles belts={belts} beltSpacing={beltSpacing} onBeltClick={handleBeltClick} />
        </div>
      </div>

      <CurriculumDialog belt={selectedBelt} curriculum={selectedCurriculum} onClose={handleCloseDialog} />
    </div>
  );
};
